/**
 * Email Us Container.
 * It helps to show the selected Topic in Breadcrumb and Render the Required Form.
 * Form is dynamic and it will be generated as per response from Topic Data API through `WebForm` Component.
 * The `WebForm` Component internally calls forms components in `public/js/contactUs/components/form`.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _isEmpty from 'lodash/isEmpty';
import _indexOf from 'lodash/indexOf';
import _find from 'lodash/find';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import { purgeStoredState } from 'redux-persist';
import { asyncSessionStorage } from 'redux-persist/storages';
import { preserveData, changeTopicTree, cancelEmailForm, sendEmail } from '../actions/actions-email-form';
import { BreadCrumb, WebForm } from '../components/common/index';
import { trackLink } from '../../lib/analytics-helper';
import * as shape from '../constants/PropTypesShapes';
import Page from '../../lib/page';
import { hideSubmitButton } from '../../lib/emailUtil';

const { emailUs } = Page.getLocaleContent('emailUs');
const isSmcEnabled = Page.getIsSmcEnabled();

class EmailUs extends Component {

  // Get the BreadCrumb Component with `Change` Link.
  // Change Link will go back to Topic Tree with `fpti` Tracking.
  getBreadCrumb() {
    return (
      <div className="bread-crumb">
        <BreadCrumb
          blockLabel={emailUs.breadCrumbTitle}
          backLinkLabel={emailUs.backLinkLabel}
          items={this.props.breadCrumbItems}
          handleChange={this.handleChangeTopicTree()}
          trackLink={{ id: 'emailPage', link: `smarthelp-email-breadcrumb-change-topic|${this.props.selectedDetails.topicId}` }}
        />
      </div>
    );
  }

  // Get the `WebForm` Component as per Topic Data API Response.
  // `WebForm` Field id will be prefixed with `type-topic-id-index`.
  // Date Picker Format will be called from locale.
  getWebForm() {
    return (
      <div className="form-fields">
        <WebForm
          fields={this.props.fieldDetails}
          submitLabel={emailUs.submit}
          resetLabel={emailUs.reset}
          errors={emailUs.errors}
          handleFieldValue={this.props.preserveData}
          handleCancel={this.cancelEmailForm()}
          handleSubmit={this.handleFormSubmit()}
          submitButtonStyle={`left-col col-xs-6 col-sm-6 col-md-4 col-lg-3
            ${hideSubmitButton(this.props.fieldDetails)}`}
        />
      </div>
    );
  }

  // Container with `BreadCrumb` and `WebForm` Component.
  getContainer() {
    let emailContainer = null;

    // Check, if it `display` property is avaiable.
    if (this.props.display) {
      emailContainer = (
        <div className="email-us-container">
          <div className="col-xs-0 col-sm-1 col-md-1 col-lg-1" />
          <div className="col-xs-12 col-sm-10 col-md-10 col-lg-10 right-col">
            <h1 className="title text-center">
              {emailUs.title}
            </h1>
            {this.getBreadCrumb()}
            {this.getWebForm()}
          </div >
        </div>
      );
    }

    return emailContainer;
  }
  // Pick the Field name and value in expected Format.
  // During error or empty param, it will return empty array.
  getCustomFieldPayLoad(formFields) { // eslint-disable-line class-methods-use-this
    let customFieldPayLoad = [];

    if (!_isEmpty(formFields) && formFields.length >= 1) {
      customFieldPayLoad = formFields.map(field => _pick(field, ['name', 'value']));
    }

    return customFieldPayLoad;
  }

  // Get Form Field data without `Message` field which is in textarea.
  // During error or empty param, it will return empty array.
  getCustomFields(formData) { // eslint-disable-line class-methods-use-this
    let formFields = [];
    let payload = [];

    if (!_isEmpty(formData) && formData.length > 1) {
      formFields = formData.filter((field) => {
        const fieldTypes = ['text', 'number', 'textarea', 'date', 'select'];
        if (_indexOf(fieldTypes, field.type) > -1) {
          return !(field.type === 'textarea' && typeof field.label === 'string' && field.label.toLowerCase() === 'message');
        }
        return false;
      });

      payload = this.getCustomFieldPayLoad(formFields);
    }

    return payload;
  }

  handleChangeTopicTree() {
    return () => {
      trackLink({ id: 'emailPage', link: `smarthelp-change-topic-tree|${this.props.selectedDetails.topicName}` });
      this.props.changeTopicTree();
    };
  }

  // This method will be passed to `WebForm` onSubmit event.
  // Payload hold the CSRF Token since it is a `POST` Call.
  handleFormSubmit() {
    return (formData) => {
      const messageField = _find(formData, { type: 'textarea' });
      trackLink({ id: 'emailPage', link: `smarthelp-submit-email-form|${this.props.selectedDetails.topicName}` });

      const payload = {
        message: _get(messageField, 'value', ''),
        topic_code: this.props.selectedDetails.kanaCode,
        subject: this.props.selectedDetails.subTopicName,
        topicName: this.props.selectedDetails.topicName,
        subTopicName: this.props.selectedDetails.subTopicName,
        custom_fields: this.getCustomFields(formData),
        _csrf: Page.getCSRFtoken(),
      };

      purgeStoredState({ storage: asyncSessionStorage }).then(() => {
      });
      const thankyouPageDescription = Page.getEmailThankYouPageDescription(this.props.topicTreeSelectionL1Reference);
      this.props.sendEmail({ emailPayload: payload, hasCommunityAccess: this.props.hasCommunityAccess, isSmcEnabled, thankyouPageDescription });
    };
  }

  // Cancel and Hide the WebForm then display the Topic Tree.
  // Trigger the `fpti` tracking.
  cancelEmailForm() {
    return () => {
      trackLink({ id: 'emailPage', link: `smarthelp-cancel-email-form|${this.props.selectedDetails.topicName}` });
      this.props.cancelEmailForm();
    };
  }

  // Render the EmailUs Container.
  render() {
    return this.getContainer();
  }
}

// Get the Store data and Pass it as Props.
function mapStateToProps(state) {
  return {
    breadCrumbItems: state.breadCrumb.items,
    hasCommunityAccess: (state.contactChannels.channels.some(channel => channel.type === 'community')),
    fieldDetails: state.emailForm.fields,
    selectedDetails: {
      topicId: state.emailForm.fieldPrefix,
      kanaCode: state.emailForm.kanaCode,
      topicName: state.emailForm.topicName,
      subTopicName: state.emailForm.subTopicName,
      pageflow: '',
    },
    display: state.emailForm.display,
    topicTreeSelectionL1Reference: state.topicTree.topicTreeSelectionL1 ? state.topicTree.topicTreeSelectionL1.ref_id : undefined,
  };
}

// EmailUs Props Types.
EmailUs.propTypes = {
  // Simple Prop Types.
  breadCrumbItems: PropTypes.arrayOf(PropTypes.string).isRequired,
  hasCommunityAccess: PropTypes.bool,

  // Complex Prop Types.
  fieldDetails: PropTypes.arrayOf(PropTypes.shape(shape.WEBFORM_FIELDS_DETAILS)),
  selectedDetails: PropTypes.shape(shape.WEBFORM_OTHER_FIELD_DETAILS),

  // Method Prop Types.
  preserveData: PropTypes.func.isRequired,
  changeTopicTree: PropTypes.func.isRequired,
  cancelEmailForm: PropTypes.func.isRequired,
  sendEmail: PropTypes.func.isRequired,
  topicTreeSelectionL1Reference: PropTypes.string,

  // Display Flag Prop Type.
  display: PropTypes.bool,
};

/**
 * Dispatch the Action as Props.
 * The `preserveData` action trigger on Every Action of Field during Value change.
 * The `changeTopicTree` dispatches OnClick of Change Topic Tree.
 * The `cancelEmailForm` dispatches Cancel WebForm and open the last selected Topic Tree.
 * The `sendEmail` dispatches the action with Form Serialized Value.
 */
function mapDispatchToProps(dispatch) {
  return bindActionCreators({ preserveData, changeTopicTree, cancelEmailForm, sendEmail }, dispatch);
}

// EmailUs Props default values.
EmailUs.defaultProps = {
  breadCrumbItems: [],
  hasCommunityAccess: false,
  fieldDetails: [],
  selectedDetails: {},
  display: false,
  topicTreeSelectionL1Reference: undefined,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmailUs);
